import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  DialogActions,
  DialogContent,
  Grid2,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "../../../../../../app/helpers";
import { RootState } from "../../../../../../app/rootReducer";
import {
  BookingServiceEditedData,
  OrderableBookableEquipmentEditedData,
  OrderableCateringEditedData,
  OrderableRoomLayoutEditedData,
  ServiceOrderDto
} from "./Domain/BookingServicePrompt.types";
import { setInputs } from "../../../../../../features/Booking-Form/slices/booking.slice";
import { hasMandatory } from "./functions/hasMandatory.function";
import { BookingServicePromptDialogContents } from "./BookingServicePromptDialogContents";

export type BookingServiceMapConfig = {
  key: string;
  label: string;
  editedData: (
    | OrderableCateringEditedData
    | OrderableBookableEquipmentEditedData
    | OrderableRoomLayoutEditedData
  )[];
  usedItems: number;
  isMandatory: boolean;
  testId: string;
  originalIndex: number;
  mappedIndex?: number;
};

interface Props {
  entryId: number;
  editedService: BookingServiceEditedData;
  setEditedService: Dispatch<SetStateAction<BookingServiceEditedData>>;
  orderedService: ServiceOrderDto;
  setOrderedService: Dispatch<SetStateAction<ServiceOrderDto>>;
  handleModalClose: () => void;
}

export function BookingServicePromptDialog({
  entryId,
  editedService,
  setEditedService,
  orderedService,
  setOrderedService,
  handleModalClose
}: Readonly<Props>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const dispatch = useDispatch();
  const { conferenceService } = useSelector((state: RootState) => state.booking.inputs);

  const [selectTab, setSelectTab] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const unmandatoryMovedRef = useRef(false);

  const mappedTabs = useMemo(() => {
    const tabConfigs: BookingServiceMapConfig[] = [
      {
        key: "catering",
        label: "Catering",
        editedData: editedService.cateringData,
        usedItems: orderedService?.caterings.filter(c => c.qty > 0).length ?? 0,
        isMandatory: hasMandatory(editedService.cateringData),
        testId: "booking-services-selection-tab0",
        originalIndex: 0
      },
      {
        key: "equipment",
        label: "Equipment",
        editedData: editedService.bookableEquipmentData,
        usedItems: orderedService?.bookableEquipments.length ?? 0,
        isMandatory: hasMandatory(editedService.bookableEquipmentData),
        testId: "booking-services-selection-tab1",
        originalIndex: 1
      },
      {
        key: "roomLayout",
        label: "Room Layout",
        editedData: editedService.roomLayoutData,
        usedItems: orderedService?.roomLayout.length ?? 0,
        isMandatory: hasMandatory(editedService.roomLayoutData),
        testId: "booking-services-selection-tab2",
        originalIndex: 2
      }
    ];
    // Filter out any tab with no count
    const visibleTabs = tabConfigs.filter(t => t.editedData.length > 0);

    return visibleTabs.map((t, index) => ({
      ...t,
      mappedIndex: index
    }));
  }, [
    editedService.cateringData,
    editedService.bookableEquipmentData,
    editedService.roomLayoutData,
    orderedService?.caterings,
    orderedService?.bookableEquipments,
    orderedService?.roomLayout
  ]);

  // Track if we've initialized the tab selection
  const hasInitializedRef = useRef(false);

  // Set loaded state when mappedTabs are available
  useEffect(() => {
    if (mappedTabs.length > 0) setIsLoaded(true);
  }, [mappedTabs.length]);

  // Initialize: select first mandatory tab, or first tab if no mandatory
  // Only run once when mappedTabs first become available
  useEffect(() => {
    if (!isLoaded || mappedTabs.length === 0 || hasInitializedRef.current) return;

    // Reset unmandatory moved flag when modal opens
    unmandatoryMovedRef.current = false;

    // Find first mandatory tab
    const firstMandatory = mappedTabs.find(tab => tab.isMandatory);

    if (firstMandatory) {
      setSelectTab(firstMandatory.mappedIndex);
    } else if (mappedTabs.length > 0) {
      // If no mandatory, select first tab
      setSelectTab(mappedTabs[0].mappedIndex);
    }

    hasInitializedRef.current = true;
  }, [isLoaded, mappedTabs.length]);

  // Reset initialization flag when entryId changes (new modal session)
  useEffect(() => {
    hasInitializedRef.current = false;
  }, [entryId]);

  // Determine button text: "Next" if current tab is mandatory and there's a next mandatory tab, otherwise "Confirm"
  const buttonText = useMemo(() => {
    const currentTab = mappedTabs.find(tab => tab.mappedIndex === selectTab);
    const isCurrentMandatory = currentTab?.isMandatory ?? false;

    // Get all mandatory tabs
    const mandatoryTabs = mappedTabs.filter(tab => tab.isMandatory);

    // If all mandatory tabs have usedItems > 0, show "Confirm"
    if (mandatoryTabs.length > 0 && mandatoryTabs.every(tab => tab.usedItems > 0)) {
      return t("Confirm");
    }

    // If current tab is mandatory, check if there's a next mandatory tab (after current index)
    if (isCurrentMandatory) {
      /**
       * const nextMandatory = mappedTabs.find(tab => tab.isMandatory && tab.mappedIndex > selectTab);
       * if (nextMandatory) { return t("Next"); };
       */
      return t("Next");
    }

    // Otherwise show "Confirm"
    return t("Confirm");
  }, [selectTab, mappedTabs, t]);

  const persistConferenceService = (order: ServiceOrderDto) => {
    order.caterings = order.caterings.filter(item => item.qty > 0);
    dispatch(setInputs({ conferenceService: { ...conferenceService, [entryId]: order } }));
  };

  const handleCancel = () => {
    // Keep mandatory auto-selections on cancel so modal does not reopen immediately
    // as "mandatory incomplete" for the same entry.
    const allMandatoryFulfilled =
      mappedTabs.some(tab => tab.isMandatory) &&
      mappedTabs.filter(tab => tab.isMandatory).every(tab => tab.usedItems > 0);

    if (allMandatoryFulfilled) {
      persistConferenceService({ ...orderedService });
    }

    handleModalClose();
  };

  const handleSave = () => {
    const order = { ...orderedService };

    // Check if current tab is mandatory
    const currentTab = mappedTabs.find(tab => tab.mappedIndex === selectTab);
    const isCurrentMandatory = currentTab?.isMandatory ?? false;

    // If current tab is unmandatory, store and close immediately
    if (!isCurrentMandatory) {
      persistConferenceService(order);
      handleModalClose();
      return;
    }

    // Current tab is mandatory - find next mandatory tab (after current index)
    const nextMandatory = mappedTabs.find(tab => tab.isMandatory && tab.mappedIndex > selectTab);

    if (nextMandatory) {
      // Go to next mandatory tab
      setSelectTab(nextMandatory.mappedIndex);
      return;
    }

    // Before proceeding, ensure all mandatory tabs have at least one ordered item
    const incompleteMandatory = mappedTabs.find(tab => tab.isMandatory && tab.usedItems <= 0);
    if (incompleteMandatory) {
      setSelectTab(incompleteMandatory.mappedIndex);
      return;
    }
    // No more mandatory tabs - go to unmandatory tab one time
    /**
     * if (!unmandatoryMovedRef.current) {
      const unmandatoryTab = mappedTabs.find(tab => !tab.isMandatory);

      if (unmandatoryTab) {
        // Go to unmandatory tab one time
        setSelectTab(unmandatoryTab.mappedIndex);
        unmandatoryMovedRef.current = true;
        return;
      }
    }
     */

    // All mandatory tabs are handled and unmandatory tab is handled (or doesn't exist)
    persistConferenceService(order);
    handleModalClose();
  };

  return (
    <>
      <DialogContent>
        <Stack gap={1} data-testid={"place-booking-detail-stack"}>
          {/* booking service prompt dialog header */}
          <Grid2 container>
            <Grid2 size={"grow"} />
            <Grid2 size={{ xs: 10 }}>
              <Typography align={"center"} lineHeight={2} variant={"h5"}>
                {t("Drawer_Services")}
              </Typography>
            </Grid2>
            <Grid2 size={{ xs: 1 }} sx={{ textAlign: "right", placeSelf: "center" }}>
              {isMobile && (
                <IconButton
                  sx={{ float: "right" }}
                  data-testid={"close-detail-modal"}
                  onClick={handleModalClose}
                  size="large"
                >
                  <Close />
                </IconButton>
              )}
            </Grid2>
          </Grid2>

          <BookingServicePromptDialogContents
            mappedTabs={mappedTabs}
            editedService={editedService}
            setEditedService={setEditedService}
            orderedService={orderedService}
            setOrderedService={setOrderedService}
            selectTab={selectTab}
            setSelectTab={setSelectTab}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: "0px 24px 20px" }}>
        <Button data-testid="cancel-booking-services-btn" onClick={handleCancel} color="secondary">
          {t("Cancel")}
        </Button>
        <Button data-testid="save-booking-services-btn" onClick={handleSave}>
          {buttonText}
        </Button>
      </DialogActions>
    </>
  );
}
